/* System defined core library */
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

/* User defined library/components */
import { AuthGuard } from '../shared/services/activation-guard.service';
import { AppointmentSearchComponent } from './appointment-search/appointment-search.component';
import { VerificationDetailComponent } from './appointment-search/verification-detail/verification-detail.component';
import { CancellationAppointmentComponent } from './cancellation-appointment/cancellation-appointment.component';
import { CancellationComponent } from './cancellation-appointment/cancellation/cancellation.component';
import { CancellationConfirmationComponent } from './cancellation-appointment/confirmation/cancellation-confirmation.component';
import { PaymentCancellationComponent } from './cancellation-appointment/payment/payment-cancellation.component';
import { CancellationVerificationComponent } from './cancellation-appointment/verification/cancellation-verification.component';
import { FindAvailabilityLocationComponent } from './find-availability/find-availability-location/find-availability-location.component';
import { FindAvailabilityProctorComponent } from './find-availability/find-availability-proctor/find-availability-proctor.component';
import { FindAvailabilityTestcenterComponent } from './find-availability/find-availability-testcenter/find-availability-testcenter.component';
import { FindAvailabilityTimezoneComponent } from './find-availability/find-availability-timezone/find-availability-timezone.component';
import { FindAvailabilityComponent } from './find-availability/find-availability.component';
import { PaymentComponent } from './reschedule-appointment/payment/payment.component';
import { ReconfirmationComponent } from './reschedule-appointment/reconfirmation/reconfirmation.component';
import { RescheduleAppointmentComponent } from './reschedule-appointment/reschedule-appointment.component';
import { RescheduleLocationComponent } from './reschedule-appointment/reschedule-location/reschedule-location.component';
import { RescheduleMultidaySessionComponent } from './reschedule-appointment/reschedule-multiday-session/reschedule-multiday-session.component';
import { RescheduleMultidayTestCenterComponent } from './reschedule-appointment/reschedule-multiday-testcenter/multiday-testcenter.component';
import { RescheduleProctorComponent } from './reschedule-appointment/reschedule-proctor/reschedule-proctor.component';
import { RescheduleTestCenterComponent } from './reschedule-appointment/reschedule-testcenter/reschedule-testcenter.component';
import { RescheduleTimezoneComponent } from './reschedule-appointment/reschedule-timezone/reschedule-timezone.component';
import { RescheduleComponent } from './reschedule-appointment/reschedule/reschedule.component';
import { BiometricConsentComponent } from './schedule-appointment/biometric-consent/biometric-consent.component';
import { CandidateProfileComponent } from './schedule-appointment/candidate-profile/candidate-profile.component';
import { CustomAgreementComponent } from './schedule-appointment/custom-agreement/custom-agreement.component';
import { MultidayTestCenterComponent } from './schedule-appointment/multiday-testcenter/multiday-testcenter.component';
import { SchedulePaymentComponent } from './schedule-appointment/payment/payment.component';
import { ProgramInformationComponent } from './schedule-appointment/program-information/program-information.component';
import { ScheduleLocationComponent } from './schedule-appointment/schedule-location/schedule-location.component';
import { ScheduleProctorComponent } from './schedule-appointment/schedule-proctor/schedule-proctor.component';
import { ScheduleTestCenterComponent } from './schedule-appointment/schedule-testcenter/schedule-testcenter.component';
import { ScheduleTimezoneComponent } from './schedule-appointment/schedule-timezone/schedule-timezone.component';
import { SchedulingComponent } from './scheduling.component';
import { AppointmentInfoComponent } from './shared/components/appointment-info/appointment-info.component';
import { ExamSelectorComponent } from './shared/components/exam-selector/exam-selector.component';
import { InvalidRequestComponent } from './shared/components/invalid-request/invalid-request.component';
import { ProgramSelectorComponent } from './shared/components/program-selector/program-selector.component';


const schedulingRoutes: Routes = [
  {
    path: 'scheduling',
    component: SchedulingComponent,
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    children: [
      { path: '', redirectTo: 'scheduling/appointment', pathMatch: 'full' },
      { path: 'searchAvailability', component: FindAvailabilityComponent, data: { title: 'Search Availability' } },
      { path: 'exam', component: ExamSelectorComponent, data: { title: 'Exam Identifier' } },
      { path: 'program', component: ProgramSelectorComponent, data: { title: 'Program Identifier' } },
      { path: 'scheduleLocation', component: ScheduleLocationComponent, data: { title: 'Date & location Selection' } },
      { path: 'scheduleTimeZone', component: ScheduleTimezoneComponent, data: { title: 'Time Zone Selection' } },
      { path: 'scheduleProctor', component: ScheduleProctorComponent, data: { title: 'Date & time Selection' } },
      { path: 'scheduleMultiday', component: MultidayTestCenterComponent, data: { title: 'Date & time Selection' } },
      {

        path: 'scheduleappointment', loadChildren: () => new Promise(resolve => {
          (require as any).ensure([], (require: any) => {
            resolve(require('./schedule-appointment/schedule-appointment.module').ScheduleModule);
          })
        })
      },

      {

        path: 'agreement', loadChildren: () => new Promise(resolve => {
          (require as any).ensure([], (require: any) => {
            resolve(require('./schedule-appointment/agreement/agreement.module').AgreementModule);
          })
        })
      },

      {
        path: 'eligibility', loadChildren: () => new Promise(resolve => {
          (require as any).ensure([], (require: any) => {
            resolve(require('./schedule-appointment/eligibility-selector/eligibility-selector.module').EligibilitySelectorModule);
          })
        })
      },

      {

        path: 'candidateSelectable', loadChildren: () => new Promise(resolve => {
          (require as any).ensure([], (require: any) => {
            resolve(require('./schedule-appointment/candidate-selectable/candidate-selectable.module').CandidateSelectorModule);
          })
        })
      },

      {
        path: 'confirmation', loadChildren: () => new Promise(resolve => {
          (require as any).ensure([], (require: any) => {
            resolve(require('./schedule-appointment/confirmation/confirmation.module').ConfirmationModule);
          })
        })
      },


      { path: 'scheduleTestcenter', component: ScheduleTestCenterComponent, data: { title: 'Test Center Selection' } },

      {
        path: 'rescheduleappointment', component: RescheduleAppointmentComponent, children: [
          { path: '', redirectTo: 'reschedule', pathMatch: 'full' },
          { path: 'reschedule', component: RescheduleComponent, data: { title: 'Reschedule Appointment' } },
          { path: 'rescheduleLocation', component: RescheduleLocationComponent, data: { title: 'Date & location Selection' } },
          { path: 'rescheduleTimeZone', component: RescheduleTimezoneComponent, data: { title: 'Time Zone Selection' } },
          { path: 'rescheduleProctor', component: RescheduleProctorComponent, data: { title: 'Date & time Selection' } },
          { path: 'rescheduleTestcenter', component: RescheduleTestCenterComponent, data: { title: 'Test Center Selection' } },
          { path: 'rescheduleMultiday', component: RescheduleMultidayTestCenterComponent, data: { title: 'Test Center Selection' } },
          { path: 'rescheduleSession', component: RescheduleMultidaySessionComponent, data: { title: 'Test Center Selection' } },
          { path: 'payment', component: PaymentComponent, data: { title: 'Reschedule Payment' } },
          { path: 'reconfirmation', component: ReconfirmationComponent, data: { title: 'Reschedule Appointment Summary' } },
        ]
      },
      {
        path: 'cancelappointment', component: CancellationAppointmentComponent, children: [
          { path: '', redirectTo: 'cancellation', pathMatch: 'full' },
          { path: 'cancellation', component: CancellationComponent, data: { title: 'Cancel Appointment' } },
          { path: 'verification', component: CancellationVerificationComponent, data: { title: 'Cancel Appointment Summary' } },
          { path: 'payment', component: PaymentCancellationComponent, data: { title: 'Cancellation Payment' } },
          { path: 'confirmation', component: CancellationConfirmationComponent, data: { title: 'Appointment Cancelled' } },
        ]
      },
      { path: 'testInformation', component: ProgramInformationComponent, data: { title: 'Program Information' } },

      { path: 'invalidRequest', component: InvalidRequestComponent, data: { title: 'Invalid request' } },

      { path: 'candidateProfile', component: CandidateProfileComponent, data: { title: 'Additional Demographic Information' } },
      { path: 'biometricConsent', component: BiometricConsentComponent, data: { title: 'Biometric Consent' } },
      { path: 'customAgreement', component: CustomAgreementComponent, data: { title: 'Agreements' } },
      { path: 'verification', component: VerificationDetailComponent, data: { title: 'Verify Appointment Information' } },

      { path: 'payment', component: SchedulePaymentComponent, data: { title: 'Schedule Payment' } },
      { path: 'findAvailability', component: FindAvailabilityComponent, data: { title: 'Search Availability' } },

      { path: 'findAvailabilityLocation', component: FindAvailabilityLocationComponent, data: { title: 'Date & location Selection' } },
      { path: 'findAvailabilityTestcenter', component: FindAvailabilityTestcenterComponent, data: { title: 'Test Center Selection' } },

      { path: 'findAvailabilityTimezone', component: FindAvailabilityTimezoneComponent, data: { title: 'Time Zone Selection' } },
      { path: 'findAvailabilityProctor', component: FindAvailabilityProctorComponent, data: { title: 'Date & time Selection' } },

      { path: 'appointmentsearch', component: AppointmentSearchComponent, data: { title: 'Search Appointment' } },
      { path: 'info', component: AppointmentInfoComponent },
    ]
  }
];

@NgModule({
  imports: [
    RouterModule.forChild(schedulingRoutes)
  ],
  exports: [
    RouterModule
  ]
})
export class SchedulingRoutingModule { }